import React from 'react';
import CTASection from '../components/CTASection';
import BenefitsSection from '../components/BenefitsSection';
import ProcessSection from '../components/ProcessSection';
import DocumentsSection from '../components/DocumentsSection';
import FAQSection from '../components/FAQSection';
import SEOMetaTags from '../components/SEOMetaTags';
import { useLanguage } from '../contexts/LanguageContext';
import usePageTitle from '../hooks/usePageTitle';

const Sell = () => {
    const { t, currentLang } = useLanguage();
    usePageTitle('sell');

    const getLangPath = (path) => {
        return currentLang && currentLang !== 'es' ? `/${currentLang}${path}` : path;
    };

    const benefits = [
        {
            icon: 'ph-bold ph-calculator',
            title: t('sell.benefits.benefit1.title'),
            desc: t('sell.benefits.benefit1.desc')
        },
        {
            icon: 'ph-bold ph-camera',
            title: t('sell.benefits.benefit2.title'),
            desc: t('sell.benefits.benefit2.desc')
        },
        {
            icon: 'ph-bold ph-handshake',
            title: t('sell.benefits.benefit3.title'),
            desc: t('sell.benefits.benefit3.desc')
        },
        {
            icon: 'ph-bold ph-shield-check',
            title: t('sell.benefits.benefit4.title'),
            desc: t('sell.benefits.benefit4.desc')
        },
        {
            icon: 'ph-bold ph-chats-circle',
            title: t('sell.benefits.benefit5.title'),
            desc: t('sell.benefits.benefit5.desc')
        },
        {
            icon: 'ph-bold ph-heart',
            title: t('sell.benefits.benefit6.title'),
            desc: t('sell.benefits.benefit6.desc')
        }
    ];

    const processSteps = [
        {
            title: t('sell.process.step1.title'),
            desc: t('sell.process.step1.desc')
        },
        {
            title: t('sell.process.step2.title'),
            desc: t('sell.process.step2.desc')
        },
        {
            title: t('sell.process.step3.title'),
            desc: t('sell.process.step3.desc')
        },
        {
            title: t('sell.process.step4.title'),
            desc: t('sell.process.step4.desc')
        },
        {
            title: t('sell.process.step5.title'),
            desc: t('sell.process.step5.desc')
        },
        {
            title: t('sell.process.step6.title'),
            desc: t('sell.process.step6.desc')
        }
    ];

    const documents = [
        t('sell.documents.item1'),
        t('sell.documents.item2'),
        t('sell.documents.item3'),
        t('sell.documents.item4'),
        t('sell.documents.item5'),
        t('sell.documents.item6')
    ];

    const faqItems = [
        { q: t('sell.faq.q1.q'), a: t('sell.faq.q1.a') },
        { q: t('sell.faq.q2.q'), a: t('sell.faq.q2.a') },
        { q: t('sell.faq.q3.q'), a: t('sell.faq.q3.a') },
        { q: t('sell.faq.q4.q'), a: t('sell.faq.q4.a') },
        { q: t('sell.faq.q5.q'), a: t('sell.faq.q5.a') },
        { q: t('sell.faq.q6.q'), a: t('sell.faq.q6.a') },
        { q: t('sell.faq.q7.q'), a: t('sell.faq.q7.a') }
    ];

    return (
        <div className="inner-page-content" data-page="sell">
            <SEOMetaTags pageKey="sell" />
            {/* Section - Inner Page Headline Start */}
            <div className="mxd-section mxd-section-inner-headline padding-text-pre-block">
                <div className="mxd-container grid-container">
                    <div className="mxd-block loading-wrap">
                        <div className="container-fluid px-0">
                            <div className="row gx-0">
                                <div className="col-12 col-xl-2 mxd-grid-item no-margin">
                                    <div className="mxd-block__name name-inner-headline loading__item">
                                        <p className="mxd-point-subtitle">
                                            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"
                                                width="20px" height="20px" viewBox="0 0 20 20" fill="currentColor">
                                                <path fill="currentColor" d="M19.6,9.6c0,0-3,0-4,0c-0.4,0-1.8-0.2-1.8-0.2c-0.6-0.1-1.1-0.2-1.6-0.6c-0.5-0.3-0.9-0.8-1.2-1.2
                        c-0.3-0.4-0.4-0.9-0.5-1.4c0,0-0.1-1.1-0.2-1.5c-0.1-1.1,0-4.4,0-4.4C10.4,0.2,10.2,0,10,0S9.6,0.2,9.6,0.4c0,0,0.1,3.3,0,4.4
                        c0,0.4-0.2,1.5-0.2,1.5C9.4,6.7,9.2,7.2,9,7.6C8.7,8.1,8.2,8.5,7.8,8.9c-0.5,0.3-1,0.5-1.6,0.6c0,0-1.2,0.1-1.7,0.2
                        c-1,0.1-4.2,0-4.2,0C0.2,9.6,0,9.8,0,10c0,0.2,0.2,0.4,0.4,0.4c0,0,3.1-0.1,4.2,0c0.4,0,1.7,0.2,1.7,0.2c0.6,0.1,1.1,0.2,1.6,0.6
                        c0.4,0.3,0.8,0.7,1.1,1.1c0.3,0.5,0.5,1,0.6,1.6c0,0,0.1,1.3,0.2,1.7c0,1,0,4.1,0,4.1c0,0.2,0.2,0.4,0.4,0.4s0.4-0.2,0.4-0.4
                        c0,0,0-3.1,0-4.1c0-0.4,0.2-1.7,0.2-1.7c0.1-0.6,0.2-1.1,0.6-1.6c0.3-0.4,0.7-0.8,1.1-1.1c0.5-0.3,1-0.5,1.6-0.6
                        c0,0,1.3-0.1,1.8-0.2c1,0,4,0,4,0c0.2,0,0.4-0.2,0.4-0.4C20,9.8,19.8,9.6,19.6,9.6L19.6,9.6z" />
                                            </svg>
                                            <span>{t('navigation.sell')}</span>
                                        </p>
                                    </div>
                                </div>
                                <div className="col-12 col-xl-8 mxd-grid-item no-margin">
                                    <div className="mxd-block__content">
                                        <div className="mxd-block__inner-headline">
                                            <h1 className="inner-headline__title animated-type loading__item">
                                                <span id="typed-strings">
                                                    <b>{t('sell.title')}</b>
                                                    <b>{t('sell.subtitle')}</b>
                                                </span>
                                                <span id="typed"></span>
                                            </h1>
                                            <p className="inner-headline__text t-large t-bright loading__item">{t('sell.description')}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Section - Inner Page Headline End */}

            {/* Section - Lo que la mejor agencia hace por ti */}
            <BenefitsSection
                title={t('sell.benefits.title')}
                subtitle={t('sell.benefits.subtitle')}
                items={benefits}
            />

            {/* Section - Cómo vendemos tu propiedad */}
            <ProcessSection
                title={t('sell.process.title')}
                subtitle={t('sell.process.subtitle')}
                steps={processSteps}
            />

            {/* Section - Documentación que necesitas */}
            <DocumentsSection
                title={t('sell.documents.title')}
                subtitle={t('sell.documents.subtitle')}
                description={t('sell.documents.description')}
                items={documents}
            />

            {/* Section - Preguntas frecuentes */}
            <FAQSection
                title={t('sell.faq.title')}
                subtitle={t('sell.faq.subtitle')}
                items={faqItems}
            />

            {/* Section - CTA Start */}
            <CTASection
                title={t('sell.cta.title')}
                description={t('sell.cta.description')}
                buttonText={t('sell.cta.button')}
                buttonTo={getLangPath('/agency/contact')}
                icon="ph-bold ph-question"
                iconType="icon"
            />
            {/* Section - CTA End */}
        </div>
    );
};

export default Sell;
